import { useState, FormEvent, KeyboardEvent } from 'react';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();

  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [capsLockOn, setCapsLockOn] = useState(false);

  // Caps Lock is a common cause of failed logins, especially on shared POS terminals (L2 fix).
  const handlePasswordKeyEvent = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(e.getModifierState('CapsLock'));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      // Mobile auto-correct/auto-complete can prepend a trailing space, which fails the
      // backend's exact-match lookup with a confusing "Invalid username or password" (M4 fix).
      await login({ ...credentials, username: credentials.username.trim() });
      // Return to the page the user originally requested, if any (H1 fix).
      const from = (location.state as { from?: Location } | null)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch {
      // Error message is handled by the store. Clear the password so it isn't left
      // visible in the input on a shared POS terminal after a failed attempt (H3 fix).
      setCredentials((prev) => ({ ...prev, password: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white p-4 rounded-full shadow-lg">
              <svg
                className="h-16 w-16 text-primary-600"
                viewBox="0 0 64 64"
                fill="currentColor"
                aria-label="Time2Dine Logo"
              >
                {/* Clock/dining bell icon */}
                <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3" />
                <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.1" />
                <path d="M32 16v18l12 8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <circle cx="32" cy="32" r="3" fill="currentColor" />
                {/* Bell top */}
                <path d="M32 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="32" cy="6" r="2" fill="currentColor" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-primary-600">
            Time2Dine
          </h1>
          <h2 className="mt-2 text-xl font-semibold text-gray-900">
            Management Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to manage your restaurants
          </p>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6 card p-8 shadow-xl" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200" role="alert">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                minLength={3}
                placeholder="Enter your username"
                className="input"
                value={credentials.username}
                onChange={(e) =>
                  setCredentials({ ...credentials, username: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={6}
                placeholder="Enter your password"
                className="input"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                onKeyDown={handlePasswordKeyEvent}
                onKeyUp={handlePasswordKeyEvent}
              />
              {capsLockOn && (
                <p className="mt-1 text-sm text-amber-600">Caps Lock is on</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full min-h-[48px] text-base font-medium"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500">
          Time2Dine Restaurant Management System
        </p>
      </div>
    </div>
  );
}
